import {createRouter, createWebHistory, LocationQueryRaw, Router} from 'vue-router';
import {applicationConfig} from '@/brief-hub/config/applicationConfig';
import {DI, getDIContainer} from '@/brief-hub/config/DependencyInjection';
import {RouteNames} from '@/brief-hub/router/route-names';
import BriefFreelancerProfileV2 from '@/brief-hub/deposit/freelancer/BriefFreelancerProfileV2.vue';
import BriefFreelancerJobTitle from '@/brief-hub/deposit/freelancer/BriefFreelancerJobTitle.vue';
import BriefFreelancerSkills from '@/brief-hub/deposit/freelancer/BriefFreelancerSkills.vue';
import BriefConditionsWhenV2 from '@/brief-hub/deposit/conditions/BriefConditionsWhenV2.vue';
import BriefConditionsWhereV2 from '@/brief-hub/deposit/conditions/BriefConditionsWhereV2.vue';
import BriefMissionDescription from '@/brief-hub/deposit/mission/BriefMissionDescription.vue';
import BriefOverview from '@/brief-hub/deposit/overview/BriefOverview.vue';
import NotFound from '@/brief-hub/common/ui/components/NotFound.vue';
import {DraftState} from '@/common/api/api-raw-types';
import BriefsManagement from '@/brief-hub/management/BriefsManagement.vue';
import EmptyComponent from './EmptyComponent.vue';
import {routesStrategy} from '@/brief-hub/router/routes-strategy';

const navigationService = getDIContainer()[DI.navigationService];

function redirectToATS(projectId: string, queryParams?: LocationQueryRaw, conversationId?: string, tab?: string) {
    let baseUrl = `/client/sourcing-projects/${projectId}/applied`;

    if (conversationId) {
        baseUrl += `/conversation-redirect/${conversationId}/${tab}`;
    }

    const redirectUrl = new URL(baseUrl + '?entry_point=brief', window.location.origin);

    let href = redirectUrl.toString();

    if (queryParams) {
        const searchParams = new URLSearchParams(queryParams as Record<string, string>);
        const queryString = searchParams.toString();

        if (queryString) {
            href += `&${queryString}`;
        }
    }

    document.location.href = href;
}

const noScrollRoutes = [RouteNames.BRIEF_DETAIL_APPLICATION_PANEL];

const routes = [
    {
        path: '/new:wildcard(.*)',
        redirect: {name: RouteNames.BRIEF_NEW},
    },
    {
        path: '/draft/new/freelancer-profile',
        name: RouteNames.BRIEF_NEW,
        component: BriefFreelancerProfileV2,
    },
    {
        path: '/draft/new:wildcard(.*)',
        redirect: {name: RouteNames.BRIEF_NEW},
    },
    {
        path: '/draft/:id/freelancer-profile',
        name: RouteNames.BRIEF_FREELANCER_PROFILE,
        component: BriefFreelancerProfileV2,
        beforeEnter: navigationService.requireDraftState(DraftState.INITIALIZED),
    },
    ...(window.features.ff4JBriefFunnelUpdateCategorySuggestion
        ? []
        : [
              {
                  path: '/draft/:id/freelancer-job-title',
                  alias: '/:id/freelancer-job-title',
                  name: RouteNames.BRIEF_FREELANCER_JOB_TITLE,
                  component: BriefFreelancerJobTitle,
                  beforeEnter: navigationService.requireDraftState(DraftState.FREELANCER_SECTION_COMPLETED),
              },
          ]),
    {
        path: '/draft/:id/freelancer-skills',
        name: RouteNames.BRIEF_FREELANCER_SKILLS,
        component: BriefFreelancerSkills,
        beforeEnter: navigationService.requireDraftState(
            window.features.ff4JBriefFunnelUpdateCategorySuggestion
                ? DraftState.FREELANCER_SECTION_COMPLETED
                : DraftState.FREELANCER_JOB_TITLE_SECTION_COMPLETED,
        ),
    },
    {
        path: '/draft/:id/conditions/when',
        name: RouteNames.BRIEF_WHEN_CONDITIONS,
        component: BriefConditionsWhenV2,
        beforeEnter: navigationService.requireDraftState(DraftState.FREELANCER_SKILLS_SECTION_COMPLETED),
    },
    {
        path: '/draft/:id/conditions/where',
        name: RouteNames.BRIEF_WHERE_CONDITIONS,
        component: BriefConditionsWhereV2,
        beforeEnter: navigationService.requireDraftState(DraftState.WHEN_CONDITIONS_SECTION_COMPLETED),
    },
    {
        path: '/draft/:id/mission/description',
        name: RouteNames.BRIEF_MISSION_DESCRIPTION,
        component: BriefMissionDescription,
        beforeEnter: navigationService.requireDraftState(DraftState.WHERE_CONDITIONS_SECTION_COMPLETED),
    },
    {
        path: '/draft/:id/overview',
        name: RouteNames.BRIEF_OVERVIEW,
        component: BriefOverview,
        beforeEnter: navigationService.requireDraftState(DraftState.MISSION_DESCRIPTION_SECTION_COMPLETED),
    },
    {
        path: '/draft/:id/:wildcard(.*)',
        redirect: {name: RouteNames.BRIEF_OVERVIEW},
    },
    {
        path: '/:id',
        redirect: {name: RouteNames.BRIEF_DETAIL},
    },
    {
        path: '/:id/details',
        name: RouteNames.BRIEF_DETAIL,
        beforeEnter: (to) => {
            redirectToATS(to.params.id, to.query);
        },
        children: [
            {
                path: 'conversation/:projectConversationId',
                redirect: {name: RouteNames.BRIEF_DETAIL_APPLICATION_PANEL},
            },
            {
                path: 'application/:projectConversationId',
                name: RouteNames.BRIEF_DETAIL_APPLICATION_PANEL,
                redirect: {name: RouteNames.BRIEF_DETAIL_APPLICATION_PANEL_MESSAGES},
                children: [
                    {
                        path: 'messages',
                        name: RouteNames.BRIEF_DETAIL_APPLICATION_PANEL_MESSAGES,
                        component: EmptyComponent,
                        beforeEnter: (to) => {
                            redirectToATS(to.params.id, to.query, to.params.projectConversationId, 'messages');
                        },
                    },
                    {
                        path: 'profile',
                        name: RouteNames.BRIEF_DETAIL_APPLICATION_PANEL_PROFILE,
                        component: EmptyComponent,
                        beforeEnter: (to) => {
                            redirectToATS(to.params.id, to.query, to.params.projectConversationId, 'profile');
                        },
                    },
                ],
            },
        ],
    },
    ...routesStrategy,
    {
        path: '/:id/:wildcard(.*)',
        redirect: {name: RouteNames.BRIEF_DETAIL},
    },
    {
        path: '/:wildcard(.*)*',
        name: RouteNames.NOT_FOUND,
        component: NotFound,
    },
];

const router: Router = createRouter({
    history: createWebHistory(applicationConfig.baseUrl),
    linkActiveClass: 'active',
    routes,
    scrollBehavior(to, from, savedPosition) {
        return savedPosition !== null &&
            (to.path === from.path || noScrollRoutes.includes(to.name as string) || noScrollRoutes.includes(from.name as string))
            ? savedPosition
            : {top: 0}; // back to page top only if path change
    },
});

export {routes};

export default router;
